import { message, Row, Switch } from 'antd';
import * as React from 'react';
import '../system-offline-deposit-settings.scss';
import { Enum } from '../../../../../core/domain';
import { getAllLevels } from '../../../../../core/repositories/user';
import { API } from '../../../../../apis';
import { getViaAuth, postViaAuth, put, responseValidator } from '../../../../../apis/api';
import { Input, inputType } from '../../../../../components/input/input';
import { faNumToEnNum } from '../../../../../core/utils/decimalExtentions';
import { Button } from '../../../../../components';
import { buttonType } from '../../../../../components/button/button';
import { IsPersianChar } from '../../../../../core/utils/stringExtentions';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import './card-to-card-settings.scss';
import { VoucherPMGoogleAuth, SettingGoogleAuthType } from '../../voucher-perfectmoney/google-auth/google-auth';

const { useState, useEffect } = React;

interface CardToCardListType {
    bankName: string;
    cardNumber: string;
    id: number; // Ensure this property is present
    isActive: boolean;
    service: Enum;
    ownerName: string;
}

interface Props {
    setCardLoading: any;
}

export const CardToCardSettings: React.FC<Props> = ({ setCardLoading }) => {
    // States
    const [services, setServices] = useState<Enum[]>([]);
    const [cardToCardList, setCardToCardList] = useState<CardToCardListType[]>([]);
    const [loadingOfflineDeposit, setLoadingOfflineDeposit] = useState<boolean>(false);
    const [isGoogleAuthOpen, setIsGoogleAuthOpen] = useState<boolean>(false);
    const [googleAuth, setGoogleAuth] = useState<SettingGoogleAuthType>();
    const [googleAuthIsLoading, setGoogleAuthIsLoading] = useState<boolean>(false);

    const onFieldChange = (serviceId: number, fieldName: string, value: string) => {
        if (fieldName === 'ownerName' && !IsPersianChar(value)) return;
        setCardToCardList((prevList) =>
            prevList.map(
                (item) =>
                    item.service.id === serviceId
                        ? { ...item, [fieldName]: value } // Update the specific field
                        : item, // Return the item unchanged
            ),
        );
    };

    const handleSubmit = async () => {
        setGoogleAuth(undefined);
        setIsGoogleAuthOpen(true);
    };

    const getData = async () => {
        setCardLoading(true);
        await getViaAuth<Enum[]>(API.enum.cardToCardService).then(async (response: any) => {
            if (responseValidator(response.status)) {
                setServices(response.value);
                const _services = response.value;
                try {
                    const response = await getViaAuth<CardToCardListType[]>(API.siteSettings.cardToCardSettings);
                    setCardLoading(false);

                    // Check if response.data is defined and an array
                    if (!response.value || !Array.isArray(response.value.items)) {
                        console.error('Invalid response data:', response.value);
                        return; // Exit if data is not valid
                    }

                    // Prepare the cardToCardList with default values if they are missing
                    const initializedSettings = _services.map((service) => {
                        const existingSetting = response.value.items.find((item) => item.service.id === service.id);
                        return {
                            service,
                            id: service.id, // Add the id property
                            cardNumber: existingSetting?.cardNumber || '', // Default to empty string if missing
                            ownerName: existingSetting?.ownerName || '', // Default to empty string if missing
                            bankName: existingSetting?.bankName || '', // Default to empty string if missing
                            isActive: existingSetting?.isActive || false, // Default to false if missing
                        };
                    });

                    setCardToCardList(initializedSettings);
                } catch (error) {
                    console.error('Error fetching card to card settings:', error);
                    setCardLoading(false);
                }
            }
        });
    };

    useEffect(() => {
        getData();
    }, []);

    const renderServiceSettings = (id: number, title: string) => {
        const setting = cardToCardList.find((item) => item.service.id === id);
        return (
            <div className={'bank-item'} key={id}>
                <h4>{title}</h4>
                <Input
                    type={inputType.text}
                    name="cardNumber"
                    label="شماره کارت"
                    maxLength={16}
                    placeholder={'شماره کارت را وارد کنید'}
                    value={setting?.cardNumber}
                    onChange={(e) => onFieldChange(id, 'cardNumber', faNumToEnNum(e.target.value))}
                />
                <Input
                    type={inputType.text}
                    name="bankAccountOwner"
                    label="نام صاحب حساب"
                    placeholder={'نام صاحب حساب را وارد کنید'}
                    value={setting?.ownerName}
                    onChange={(e) => onFieldChange(id, 'ownerName', e.target.value)}
                />
                <Input
                    type={inputType.text}
                    name="bankName"
                    label="نام بانک"
                    placeholder={'نام بانک وارد کنید'}
                    value={setting?.bankName}
                    onChange={(e) => onFieldChange(id, 'bankName', e.target.value)}
                />
                <label>وضعیت :</label>
                <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    checked={setting?.isActive}
                    onChange={(checked) => {
                        setCardToCardList((prev) =>
                            prev.map(
                                (item) =>
                                    item.service.id === id // Ensure you're checking against the correct id
                                        ? { ...item, isActive: checked } // Update the isActive property
                                        : item, // Return the item unchanged
                            ),
                        );
                    }}
                />
            </div>
        );
    };

    return (
        <>
            <Row style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-evenly' }}>
                <h2>تنظیمات کارت به کارت</h2>
                <div className={'card-to-card-setting-forms-container'}>
                    {cardToCardList && services?.map((item) => renderServiceSettings(item.id, item.title))}
                </div>
                <div className="card-to-card-setting-forms-container-btn">
                    <Button
                        onClick={handleSubmit}
                        text="ویرایش تنظیمات"
                        type={buttonType.info}
                        htmlType="button"
                        loading={loadingOfflineDeposit}
                        disabled={loadingOfflineDeposit}
                    />
                </div>
            </Row>
            <VoucherPMGoogleAuth
                submitData={() => {
                    setIsGoogleAuthOpen(false);
                    const body = cardToCardList
                        .filter((item) => !!item.cardNumber)
                        .map((item) => ({ ...item, service: item.service.id }));

                    setGoogleAuthIsLoading(true);
                    postViaAuth(`${API.siteSettings.updateCardToCardSettings}`, {
                        cardToCardItems: body,
                        googleAuthCode: googleAuth?.code,
                    })
                        .then((response: any) => {
                            setGoogleAuthIsLoading(false);
                            if (responseValidator(response.status)) {
                                message.success('تنظیمات مورد نظر با موفقیت ویرایش شد.');
                                getData();
                            } else {
                                setGoogleAuth((prev) => ({ ...prev, error: 'خطا در تایید کد امنیتی. دوباره تلاش کنید.' }));
                                setIsGoogleAuthOpen(true);
                            }
                        })
                        .catch(() => {
                            setGoogleAuthIsLoading(false);
                            setGoogleAuth((prev) => ({ ...prev, error: 'ارتباط با سرور برقرار نشد.' }));
                            setIsGoogleAuthOpen(true);
                        });
                }}
                data={googleAuth}
                isOpen={isGoogleAuthOpen}
                setIsOpen={setIsGoogleAuthOpen}
                setData={setGoogleAuth}
                loading={googleAuthIsLoading}
            />
        </>
    );
};
